/**
 * Sökbar dropdown (combobox) — återanvändbar för långa listor.
 * Listan renderas via portal så den ligger ovanpå tabellrader (overflow).
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export interface SearchableSelectItem {
  id: string;
  label: string;
}

interface SearchableSelectProps {
  items: SearchableSelectItem[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  emptyOptionLabel?: string;
  disabled?: boolean;
  className?: string;
  /** Extern söksträng (t.ex. debounced API-sök) — filtrerar inte lokalt om satt */
  externalSearch?: string;
  onSearchChange?: (query: string) => void;
  isLoading?: boolean;
}

interface DropdownRect {
  top: number;
  left: number;
  width: number;
}

export function SearchableSelect({
  items,
  value,
  onChange,
  placeholder = '',
  emptyOptionLabel,
  disabled = false,
  className = '',
  externalSearch,
  onSearchChange,
  isLoading = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState('');
  const [rect, setRect] = useState<DropdownRect | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const search = externalSearch !== undefined ? externalSearch : localSearch;

  const filtered = useMemo(() => {
    if (onSearchChange) return items;
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((i) => i.label.toLowerCase().includes(q));
  }, [items, search, onSearchChange]);

  const selectedLabel =
    items.find((i) => i.id === value)?.label ?? (value === '' && emptyOptionLabel ? emptyOptionLabel : '');

  const updateRect = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 180) });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateRect();
    const onScrollOrResize = () => updateRect();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
      if (!onSearchChange) setLocalSearch('');
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onSearchChange]);

  const handleSearchInput = (next: string) => {
    if (onSearchChange) onSearchChange(next);
    else setLocalSearch(next);
  };

  const closeAndClear = () => {
    setOpen(false);
    handleSearchInput('');
  };

  const dropdown =
    open && !disabled && rect
      ? createPortal(
          <ul
            ref={listRef}
            className="fixed z-[100] max-h-60 overflow-auto rounded-lg border border-border bg-bgElevated shadow-lg"
            style={{ top: rect.top, left: rect.left, width: rect.width }}
          >
            {emptyOptionLabel != null && (
              <li
                className={`px-3 py-2 text-sm cursor-pointer hover:bg-bgBase ${
                  value === '' ? 'bg-primary/10 font-medium text-primary' : 'text-textSecondary'
                }`}
                onMouseDown={() => {
                  onChange('');
                  closeAndClear();
                }}
              >
                {emptyOptionLabel}
              </li>
            )}
            {isLoading && <li className="px-3 py-2 text-sm text-textTertiary">…</li>}
            {!isLoading && filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-textTertiary">—</li>
            )}
            {!isLoading &&
              filtered.map((item) => (
                <li
                  key={item.id}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-bgBase ${
                    item.id === value ? 'bg-primary/10 font-medium text-primary' : 'text-textDefault'
                  }`}
                  onMouseDown={() => {
                    onChange(item.id);
                    closeAndClear();
                  }}
                >
                  {item.label}
                </li>
              ))}
          </ul>,
          document.body
        )
      : null;

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <div
        ref={triggerRef}
        className={`flex items-center gap-1 px-3 py-2 border border-border rounded-lg bg-bgLight text-textDefault w-full min-h-[2.5rem] ${
          disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
        }`}
        onClick={() => {
          if (disabled) return;
          setOpen(!open);
          if (!open) setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        <MagnifyingGlassIcon className="w-4 h-4 text-textTertiary shrink-0" />
        {open ? (
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => handleSearchInput(e.target.value)}
            className="flex-1 bg-transparent outline-none text-sm text-textDefault min-w-0"
            placeholder={placeholder}
            disabled={disabled}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="flex-1 text-sm truncate">{selectedLabel || '—'}</span>
        )}
      </div>
      {dropdown}
    </div>
  );
}

export default SearchableSelect;
